/**
 * Token storage for the Auth0 access token used by apiClient.
 *
 * Storage backend: hybrid in-memory + localStorage. In-memory is the
 * read-through cache; localStorage gives us cross-refresh persistence
 * and cross-tab sync via the `storage` event.
 *
 * Security posture (worth being explicit about, since this is the
 * subtle bit):
 *
 * - **localStorage is XSS-readable.** Any script that successfully
 *   injects into our origin can read the token and impersonate the
 *   user until it expires. The two mitigations in place are:
 *
 *     1. JWT expiry is parsed at set-time and re-checked at every
 *        `getToken()` call; an expired token is removed and treated
 *        as "no token". This caps the blast radius of a leaked token
 *        to its remaining lifetime.
 *     2. CSP on the Express side (server/server.js, see helmet
 *        config) restricts script-src to first-party + the known
 *        Auth0 / Firebase / Google Maps CDNs, narrowing the XSS
 *        injection surface in the first place.
 *
 * - **The alternative (HttpOnly cookie)** would be unreadable to JS
 *   and immune to XSS-token-exfil, but would require the server to
 *   set the cookie on a session endpoint, add CSRF protection on
 *   mutating routes, and lose the cross-tab sync we get for free
 *   from localStorage. Not worth the migration today; revisit if
 *   threat model changes.
 *
 * Features layered on top of the storage choice:
 * - Token expiration validation (parses JWT `exp`, expires 60s
 *   ahead of actual to avoid races against a request in flight).
 * - Cross-tab synchronization via `storage` events.
 * - Event-driven token-change listeners for callers that need to
 *   react to login/logout without polling.
 */

// Storage keys for localStorage
const TOKEN_STORAGE_KEY = 'auth_token';
const TOKEN_EXPIRY_KEY = 'auth_token_expiry';

// In-memory storage for the token
let authToken: string | null = null;
let tokenExpiry: number | null = null;

// Event listeners for token changes
type TokenChangeListener = (token: string | null) => void;
const tokenChangeListeners: Set<TokenChangeListener> = new Set();

/**
 * Parse JWT token to extract expiration time
 * @param token - The JWT token
 * @returns Expiration timestamp in milliseconds, or null if invalid
 */
const parseTokenExpiry = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch (e) {
    console.warn('Failed to parse token expiry:', e);
    return null;
  }
};

/**
 * Check if token is expired
 * @param expiry - Token expiry timestamp in milliseconds
 * @returns True if token is expired
 */
const isTokenExpired = (expiry: number | null): boolean => {
  if (!expiry) return false;
  return Date.now() >= expiry - 60000; // Consider expired 1 minute before actual expiry
};

/**
 * Notify all listeners about token changes
 * @param token - The new token value
 */
const notifyTokenChange = (token: string | null): void => {
  tokenChangeListeners.forEach((listener) => {
    try {
      listener(token);
    } catch (e) {
      console.error('Error in token change listener:', e);
    }
  });
};

/**
 * Initialize token from localStorage with validation
 */
const initializeToken = (): void => {
  try {
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    const storedExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (storedToken) {
      const expiry = storedExpiry ? parseInt(storedExpiry, 10) : parseTokenExpiry(storedToken);

      if (expiry && isTokenExpired(expiry)) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
      } else {
        // Token is valid
        authToken = storedToken;
        tokenExpiry = expiry;
      }
    }
  } catch (e) {
    console.warn('Could not access localStorage for token initialization:', e);
  }
};

// Initialize token on module load
initializeToken();

// Listen for storage events to sync across tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === TOKEN_STORAGE_KEY) {
      const newToken = e.newValue;
      if (newToken !== authToken) {
        if (newToken) {
          const expiry = parseTokenExpiry(newToken);
          if (!expiry || !isTokenExpired(expiry)) {
            authToken = newToken;
            tokenExpiry = expiry;
            notifyTokenChange(authToken);
          }
        } else {
          authToken = null;
          tokenExpiry = null;
          notifyTokenChange(null);
        }
      }
    }
  });
}

/**
 * Set the authentication token
 * @param token - The JWT token or null to clear
 */
export const setToken = (token: string | null): void => {
  const previousToken = authToken;

  if (token) {
    // Parse and validate the token
    const expiry = parseTokenExpiry(token);

    if (expiry && isTokenExpired(expiry)) {
      console.warn('Attempting to set an expired token');
      return;
    }

    authToken = token;
    tokenExpiry = expiry;

    // Store in localStorage for persistence
    try {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      if (expiry) {
        localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
      } else {
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
      }
    } catch (e) {
      console.warn('Could not access localStorage for token storage:', e);
    }
  } else {
    // Clear the token
    authToken = null;
    tokenExpiry = null;

    try {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
    } catch (e) {
      console.warn('Could not access localStorage for token removal:', e);
    }
  }

  // Notify listeners if token changed
  if (previousToken !== authToken) {
    notifyTokenChange(authToken);
  }
};

/**
 * Get the current authentication token with expiry validation
 * @returns The JWT token or null if not set or expired
 */
export const getToken = (): string | null => {
  if (authToken && tokenExpiry && isTokenExpired(tokenExpiry)) {
    clearToken();
    return null;
  }

  if (!authToken) {
    try {
      const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
      const storedExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

      if (storedToken) {
        const expiry = storedExpiry ? parseInt(storedExpiry, 10) : parseTokenExpiry(storedToken);

        if (expiry && isTokenExpired(expiry)) {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          localStorage.removeItem(TOKEN_EXPIRY_KEY);
          return null;
        }

        // Token is valid, load it into memory
        authToken = storedToken;
        tokenExpiry = expiry;
      }
    } catch (e) {
      console.warn('Could not access localStorage for token retrieval:', e);
    }
  }

  return authToken;
};

/**
 * Clear the authentication token
 */
export const clearToken = (): void => {
  const previousToken = authToken;

  authToken = null;
  tokenExpiry = null;

  // Also clear from localStorage
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  } catch (e) {
    console.warn('Could not access localStorage for token removal:', e);
  }

  // Notify listeners if token was cleared
  if (previousToken !== null) {
    notifyTokenChange(null);
  }
};

/**
 * Subscribe to token changes
 * @param listener - Function to call when token changes
 * @returns Unsubscribe function
 */
export const onTokenChange = (listener: TokenChangeListener): (() => void) => {
  tokenChangeListeners.add(listener);

  // Return unsubscribe function
  return () => {
    tokenChangeListeners.delete(listener);
  };
};
